import { Link, useLocation } from "react-router-dom"
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  FileQuestion,
  Flag,
  CreditCard,
  Package,
  BarChart3,
  Settings,
  FolderOpen,
  TrendingUp,
  Mail,
  Megaphone,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useSelector } from "react-redux"
import { RootState } from "@/store"
import { UserRole } from "../enums"

interface DashboardNavProps {
  closeSheet?: () => void
}

interface NavItem {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  requiredRoles: string[]
}

interface NavGroup {
  title: string
  items: NavItem[]
}

const navGroups: NavGroup[] = [
  {
    title: "",
    items: [
      { title: "Dashboard", href: "/dashboard", icon: LayoutDashboard, requiredRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.QUESTION_MANAGER] },
      { title: "Progress Tracker", href: "/dashboard/progress-tracker", icon: BarChart3, requiredRoles: [UserRole.SUPER_ADMIN] }
    ],
  },
  {
    title: "Overview",
    items: [
      { title: "Orders", href: "/dashboard/orders", icon: Package, requiredRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN] },
      { title: "Plans", href: "/dashboard/plans", icon: Package, requiredRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN] },
      { title: "Payments", href: "/dashboard/payments", icon: CreditCard, requiredRoles: [UserRole.SUPER_ADMIN] },
      { title: "Subscriptions", href: "/dashboard/subscriptions", icon: TrendingUp, requiredRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN] },
      { title: "Email Logs", href: "/dashboard/email-logs", icon: Mail, requiredRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN] },
    ],
  },
  {
    title: "Questions",
    items: [
      { title: "Question Bank", href: "/dashboard/question-bank", icon: FileQuestion, requiredRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.QUESTION_MANAGER] },
      // Roles match the API: the reports carry other students' emails and notes,
      // so the queue endpoint is ADMIN / SUPER_ADMIN only.
      { title: "Flagged Questions", href: "/dashboard/flagged-questions", icon: Flag, requiredRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN] },
    ],
  },
  {
    title: "Management",
    items: [
      // Same roles as Source Management: what students see is written here.
      { title: "Announcements & Ads", href: "/dashboard/announcements", icon: Megaphone, requiredRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN] },
      { title: "Coupon Management", href: "/dashboard/coupon", icon: CreditCard, requiredRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN] },
      { title: "Exam Builder", href: "/dashboard/exam-builder", icon: GraduationCap, requiredRoles: [UserRole.SUPER_ADMIN] },
      { title: "Interest Management", href: "/dashboard/interest-management", icon: Settings, requiredRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN] },
      { title: "Source Management", href: "/dashboard/source-management", icon: FolderOpen, requiredRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN] },
      { title: "User Management", href: "/dashboard/users", icon: Users, requiredRoles: [UserRole.SUPER_ADMIN, UserRole.ADMIN] },
    ],
  },
]

function getUserRole(roles: string[]): string {
  if (roles.includes(UserRole.SUPER_ADMIN)) return UserRole.SUPER_ADMIN
  if (roles.includes(UserRole.ADMIN)) return UserRole.ADMIN
  if (roles.includes(UserRole.QUESTION_MANAGER)) return UserRole.QUESTION_MANAGER
  return "TRIAL"
}

export function DashboardNav({ closeSheet }: DashboardNavProps) {
  const location = useLocation()
  const roles = useSelector((state: RootState) => state.auth.roles)

  const userRole = getUserRole(roles)

  return (
    <nav className="grid items-start gap-4">
      {navGroups.map((group) => {
        const filteredItems = group.items.filter((item) =>
          item.requiredRoles?.includes(userRole)
        )
        if (filteredItems.length === 0) return null

        return (
          <div key={group.title} className="grid gap-1">
            <h4 className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {group.title}
            </h4>
            <div className="grid items-start gap-1">
              {filteredItems.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.href
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={closeSheet}
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground",
                      isActive
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {item.title}
                  </Link>
                )
              })}
            </div>
          </div>
        )
      })}
    </nav>
  )
}

export default DashboardNav
